/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import TextField from '@material-ui/core/TextField';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { scaffolderTranslationRef } from '@backstage/plugin-scaffolder/alpha';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import { uniqueEntityNamePickerValidation } from './validation';
import {
  UniqueEntityNamePickerProps,
  UniqueEntityNamePickerSchema,
} from './schema';

/**
 * EntityName Picker
 */
const UniqueEntityNamePicker = (props: UniqueEntityNamePickerProps) => {
  const { t } = useTranslationRef(scaffolderTranslationRef);
  const {
    onChange,
    required,
    schema: {
      title = t('fields.entityNamePicker.title'),
      description = t('fields.entityNamePicker.description'),
    },
    rawErrors,
    formData,
    uiSchema: { 'ui:autofocus': autoFocus },
    idSchema,
    placeholder,
  } = props;

  return (
    <TextField
      id={idSchema?.$id}
      label={title}
      placeholder={placeholder}
      helperText={description}
      required={required}
      value={formData ?? ''}
      onChange={({ target: { value } }) => onChange(value)}
      margin="normal"
      error={rawErrors?.length > 0 && !formData}
      inputProps={{ autoFocus }}
      FormHelperTextProps={{ margin: 'dense', style: { marginLeft: 0 } }}
    />
  );
};

export const UniqueEntityNamePickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: UniqueEntityNamePicker,
    name: 'UniqueEntityNamePicker',
    schema: UniqueEntityNamePickerSchema,
    validation: uniqueEntityNamePickerValidation,
  }),
);
